import { useRef, useState } from "react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import { FileText, Trash2, Upload, ExternalLink } from "lucide-react";

interface BadgerFlyerCardProps {
  eventId: string;
  flyerUrl: string | null | undefined;
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
}

export function BadgerFlyerCard({
  eventId: _eventId,
  flyerUrl,
  onUpload,
  onDelete,
}: BadgerFlyerCardProps) {
  const { confirm, confirmDialog } = useConfirm();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== "application/pdf") return;
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async () => {
    if (
      !(await confirm(
        "Remove this flyer? The public Download Application button will stop working.",
      ))
    )
      return;
    await onDelete();
  };

  return (
    <Card id="badger-flyer">
      <CardHeader>
        <CardTitle className="text-lg">Badger Flyer</CardTitle>
        <CardDescription>
          PDF shown on the public site&apos;s &ldquo;Download Application&rdquo; button. Upload a
          new file to replace the existing one.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3">
          {flyerUrl ? (
            <div className="flex items-center gap-3 rounded-md border p-3">
              <FileText className="size-8 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">Flyer uploaded</p>
                <a
                  href={flyerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Preview
                  <ExternalLink className="size-3" />
                </a>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload className="size-4 mr-1" />
                  Replace
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={handleDelete}
                  disabled={uploading}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              className="w-full max-w-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="size-4 mr-2" />
              {uploading ? "Uploading..." : "Upload PDF flyer"}
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </CardContent>
      {confirmDialog}
    </Card>
  );
}
